import { getShiftLabel, getWorkdayLabel } from '@/entities/questionnaire';
import { QuestionKey, TimeUnit } from '@/entities/questionnaire';
import { useDictionary } from '@/shared/lib/i18n/use-dictionary';
import { useLanguage } from '@/shared/lib/i18n/use-language';
import { useCallback, useMemo } from 'react';

export function useReviewQuestionnaireLabels() {
  const dictionary = useDictionary();
  const language = useLanguage() as 'en' | 'nl';

  const questions: Record<QuestionKey, string> = useMemo(
    () => ({
      [QuestionKey.READY_FOR_JOB_OFFERS]: dictionary.areYouCurrentlyOpenToWork,
      [QuestionKey.CERTIFICATES_AND_LICENSES]: dictionary.doYouHaveFollowingCertificates,
      [QuestionKey.SALARY]: dictionary.isThisIncomeAcceptableForYou,
      [QuestionKey.SHIFTS]: dictionary.areYouAvailableForThisSchedule,
      [QuestionKey.AVAILABLE_DAYS]: dictionary.areYouAvailableForThisSchedule,
      [QuestionKey.LIMITATIONS_AND_PREFERENCES]: dictionary.areYouAbleToPerformTheseSpecificJobTasks,
      [QuestionKey.WORKING_HOURS_PER_WEEK]: dictionary.doTheseHoursPerWeekWorkForYou,
      [QuestionKey.LANGUAGES]: dictionary.doYouSpeakTheseLanguages,
    }),
    [dictionary],
  );

  const answeredQuestions: Record<QuestionKey, string | ((value: string) => string)> = useMemo(
    () => ({
      [QuestionKey.READY_FOR_JOB_OFFERS]: dictionary.areYouCurrentlyOpenToWork,
      [QuestionKey.CERTIFICATES_AND_LICENSES]: dictionary.doYouHaveAnyOtherCertificates,
      [QuestionKey.SALARY]: dictionary.whatIsYourDesiredSalaryRange,
      [QuestionKey.SHIFTS]: dictionary.doYouHaveAnyOtherShiftAvailable,
      [QuestionKey.AVAILABLE_DAYS]: dictionary.whatAreYouAvailableDays,
      [QuestionKey.LIMITATIONS_AND_PREFERENCES]: dictionary.areThereAnyOtherTasksYouCanDo,
      [QuestionKey.WORKING_HOURS_PER_WEEK]: dictionary.howManyHoursPerWeekWouldYouLikeToWork,
      [QuestionKey.LANGUAGES]: dictionary.whichLanguagesDoYouSpeak,
    }),
    [dictionary],
  );

  const timeUnitFrequencies: Record<TimeUnit, string> = useMemo(
    () => ({
      [TimeUnit.WEEK]: dictionary.weekly,
      [TimeUnit.DAY]: dictionary.daily,
      [TimeUnit.HOUR]: dictionary.hourly,
      [TimeUnit.MONTH]: dictionary.monthly,
      [TimeUnit.YEAR]: dictionary.yearly,
    }),
    [dictionary],
  );

  const getQuestionText = useCallback(
    (questionKey: QuestionKey, isAnswered?: boolean, unit?: string): string => {
      if (isAnswered) {
        const answered = answeredQuestions[questionKey];
        if (typeof answered === 'function') {
          return answered(unit ?? '');
        }
        return answered ?? questions[questionKey];
      }
      return questions[questionKey];
    },
    [questions, answeredQuestions],
  );

  const getTimeUnitFrequency = useCallback(
    (unit: TimeUnit): string => {
      return timeUnitFrequencies[unit] ?? '';
    },
    [timeUnitFrequencies],
  );

  const getShiftLabelForValue = useCallback(
    (value: string): string => {
      return getShiftLabel(value, language);
    },
    [language],
  );

  const getWorkdayLabelForValue = useCallback(
    (value: string): string => {
      return getWorkdayLabel(value, language);
    },
    [language],
  );

  return {
    getQuestionText,
    getTimeUnitFrequency,
    getShiftLabel: getShiftLabelForValue,
    getWorkdayLabel: getWorkdayLabelForValue,
    mustHaveLabel: dictionary.questionnaireMustHave,
    niceToHaveLabel: dictionary.questionnaireNiceToHave,
    tooltipLabel: dictionary.pleaseReviewTheQualificationQuestions,
    loadingTitle: dictionary.loadingQualificationQuestionary,
    backLabel: dictionary.back,
    confirmLabel: dictionary.confirmAndNext,
    title: dictionary.reviewQualificationQuestionary,
    subtitle: dictionary.pleaseReviewTheQualificationQuestions,
    certificationsPlaceholder: dictionary.certificationsPlaceholder,
    shiftPlaceholder: dictionary.shiftPlaceholder,
    workdaysPlaceholder: dictionary.workdaysPlaceholder,
    languagesPlaceholder: dictionary.languagesPlaceholder,
  };
}
